import { getI18n } from '@/plugins/i18n'

import * as dataGrid from '@/composables/data-grid'
import * as dataGridColumn from '@/composables/data-grid-column'

import { usePortalStore } from '@/stores/portal.store'

export const IMPORT_ACTION = Object.freeze({
  DRY_RUN: 'dryRun',
  EXECUTE: 'execute',
  RETRY: 'retry',
  STOP: 'stop',
})

export const getImportJobActions = (status) => {
  const importStatus = usePortalStore().importExportStatusEnum
  switch (status) {
    case importStatus.READY:
    case importStatus.CANCELLED:
    case importStatus.DRY_RUN_COMPLETED:
    case importStatus.DONE:
      return [IMPORT_ACTION.EXECUTE, IMPORT_ACTION.DRY_RUN]
    case importStatus.ERROR:
      return [IMPORT_ACTION.RETRY, IMPORT_ACTION.DRY_RUN]
    case importStatus.RUNNING:
    case importStatus.DRY_RUN_RUNNING:
      return [IMPORT_ACTION.STOP]
    default:
      return []
  }
}

export const useColumns = ({ onViewResult, onToggleJob, isJobUpdating } = {}) => {
  const i18n = getI18n()
  const portalStore = usePortalStore()
  const importStatus = portalStore.importExportStatusEnum

  return [
    {
      type: 'buttons',
      width: '12rem',
      visibleIndex: 0,
      fixed: false,
      buttons: [
        {
          template: 'importJobActionTemplate',
          visible: ({ row }) => getImportJobActions(row.data.status).length > 0,
        },
        {
          name: 'delete',
          visible: ({ row }) => ![importStatus.RUNNING, importStatus.DRY_RUN_RUNNING, importStatus.DONE].includes(row.data.status),
        },
        {
          icon: 'mdi mdi-cancel',
          hint: i18n.t('common.importExportAction.disable'),
          onClick: ({ row }) => onToggleJob?.(row.data),
          visible: ({ row }) => row.data.status !== importStatus.DISABLED,
          disabled: ({ row }) => [importStatus.RUNNING, importStatus.DRY_RUN_RUNNING].includes(row.data.status) || !!isJobUpdating?.(row.data.id),
        },
        {
          icon: 'mdi mdi-check-circle-outline',
          hint: i18n.t('common.importExportAction.enable'),
          onClick: ({ row }) => onToggleJob?.(row.data),
          visible: ({ row }) => row.data.status === importStatus.DISABLED,
          disabled: ({ row }) => !!isJobUpdating?.(row.data.id),
        },
        {
          icon: 'mdi mdi-file-chart-outline',
          hint: i18n.t('common.importExportAction.viewResult'),
          onClick: ({ row }) => onViewResult?.(row.data),
          visible: ({ row }) => [importStatus.DONE, importStatus.DRY_RUN_COMPLETED, importStatus.CANCELLED, importStatus.ERROR].includes(row.data.status) || !!row.data.last_run_at,
        },
      ],
    },
    {
      dataField: 'status',
      caption: i18n.t('common.status'),
      lookup: dataGrid.useLookup({ store: portalStore.getLocalizedImportExportStatuses(), displayExpr: 'name' }),
      cellTemplate: 'importExportStatusCellTemplate',
      allowGrouping: false,
    },
    {
      dataField: 'read_count',
      allowGrouping: false,
      caption: i18n.t('common.importResult.read_count'),
      allowFiltering: false,
      allowSorting: false,
      width: '8rem',
    },
    {
      dataField: 'operation_type',
      caption: i18n.t('common.operationType'),
      lookup: dataGrid.useLookup({ store: portalStore.getLocalizedOperationTypes(), displayExpr: 'name'}),
    },
    {
      dataField: 'track_type',
      caption: i18n.t('common.trackType'),
      lookup: dataGrid.useLookup({ store: portalStore.getLocalizedTrackTypes(), displayExpr: 'name' }),
      width: '8rem',
    },
    {
      dataField: 'source_file_name',
      caption: i18n.t('common.fileName'),
      cellTemplate: 'attachmentFileNameTemplate',
    },
    dataGridColumn.useDateTimeColumn({
      sortOrder: 'desc',
      dataField: 'last_run_at',
      caption: i18n.t('common.lastRunAt'),
      width: '8rem',
      allowGrouping: false,
    }),
    dataGridColumn.useDateTimeColumn({
      sortOrder: 'desc',
      dataField: 'next_run_at',
      caption: i18n.t('common.nextRunAt'),
      width: '8rem',
      allowGrouping: false,
    }),
    {
      dataField: 'error_message',
      caption: i18n.t('common.error'),
      dataType: 'string',
      cellTemplate: 'importErrorTemplate',
      alignment: 'center',
      width: '4rem',
      allowResizing: false,
      allowGrouping: false,
    },
  ]
}
